import { FaCalendarAlt, FaRandom, FaSearch } from 'react-icons/fa';
import { BsGraphDown, BsGraphUp } from 'react-icons/bs';
import { TbGpsFilled } from 'react-icons/tb';
import {
  MdAlignHorizontalLeft,
  MdAlignHorizontalRight,
  MdAlignVerticalBottom,
  MdBarChart,
  MdSportsSoccer,
} from 'react-icons/md';
import React, { useEffect, useRef, useState } from 'react';
import { MatchList } from './MatchList';
import {
  getDrawPredictions,
  getExactResultPredictions,
  getGoalGoalPredictions,
  getNoGoalPredictions,
  getOver2Point5Predictions,
  getUnder3Point5Predictions,
  MainMatchesPrevisionData,
  getOver1Point5Predictions,
  getMultigol2_5Predictions,
  getMultigol1_3Predictions,
  getMultigol1_3HomePredictions,
  getMultigol1_3AwayPredictions,
  getUnder1Point5HomePredictions,
  getUnder1Point5AwayPredictions,
} from './MainMatchesData';
import { TailSpin } from 'react-loader-spinner';
import { FaFutbol } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const saveWithExpiration = (
  key: string,
  value: string,
  expirationHours: number,
) => {
  const item = {
    value: value,
    expiry: Date.now() + expirationHours * 60 * 60 * 1000,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

const getWithExpiration = (key: string) => {
  const itemStr = localStorage.getItem(key);

  if (!itemStr) {
    return null;
  }

  try {
    const item = JSON.parse(itemStr);
    const now = Date.now();

    if (now > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  } catch (e) {
    localStorage.removeItem(key);
    return null;
  }
};

const MainPage = () => {
  const [selectedDate, setSelectedDate] = useState(() => {
    const savedDate = getWithExpiration('selectedDate');
    return savedDate || new Date().toISOString().split('T')[0];
  });

  const [selectedBet, setSelectedBet] = useState(
    localStorage.getItem('selectedBet') || 'GoalGoal',
  );

  const [selectedLeague, setSelectedLeague] = useState(
    localStorage.getItem('selectedLeague') || '',
  );

  const [matches, setMatches] = useState<MainMatchesPrevisionData[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const dateInputRef = useRef<HTMLInputElement>(null);
  const leagueInputRef = useRef<HTMLSelectElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const leagues = [
    { id: 'Bundesliga 1', name: 'Bundesliga 1' },
    { id: 'Bundesliga 2', name: 'Bundesliga 2' },
    { id: 'Eredivisie', name: 'Eredivisie' },
    { id: 'La Liga', name: 'La Liga' },
    { id: 'League 2', name: 'League 2' },
    { id: 'Liga Portugal', name: 'Liga Portugal' },
    { id: 'Ligue 1', name: 'Ligue 1' },
    { id: 'Premier League', name: 'Premier League' },
    { id: 'Serie A', name: 'Serie A' },
    { id: 'Serie B', name: 'Serie B' },
    { id: 'Super League', name: 'Super League' },
    { id: 'Super Lig', name: 'Super Lig' },
    { id: 'MLS', name: 'MLS' },
    { id: 'Allsvenskan', name: 'Allsvenskan' },
    { id: 'Eliteserien', name: 'Eliteserien' },
    { id: 'Veikkausliiga', name: 'Veikkausliiga' },
    { id: 'Premier Division', name: 'Premier Division' },
    { id: 'Superliga', name: 'Superliga' },
    { id: 'Ekstraklasa', name: 'Ekstraklasa' },
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 700);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    saveWithExpiration('selectedDate', selectedDate, 2);
  }, [selectedDate]);

  useEffect(() => {
    localStorage.setItem('selectedBet', selectedBet);
  }, [selectedBet]);

  useEffect(() => {
    localStorage.setItem('selectedLeague', selectedLeague);
  }, [selectedLeague]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    if (!getWithExpiration('selectedDate')) {
      setSelectedDate(today);
    }
    if (dateInputRef.current) {
      if (selectedDate === today) {
        dateInputRef.current.classList.remove(
          'bg-gradient-to-r',
          'from-teal-600',
          'to-teal-500',
          'text-white',
          'border',
          'border-teal-700',
          'transform',
          'scale-105',
          'bg-customPurple',
        );
      } else {
        // Applica il gradiente verde quando la data è diversa da oggi
        dateInputRef.current.classList.add(
          'bg-gradient-to-r',
          'from-teal-600',
          'to-teal-500',
          'text-white',
          'border',
          'border-teal-700',
          'transform',
          'scale-105',
        );
      }
    }
    const leagueVal = '';
    if (!localStorage.getItem('selectedLeague')) {
      setSelectedLeague(leagueVal);
    }
    if (leagueInputRef.current) {
      if (selectedLeague === 'All') {
        leagueInputRef.current.classList.remove(
          'bg-gradient-to-r',
          'from-teal-600',
          'to-teal-500',
          'text-white',
          'border',
          'border-teal-700',
          'transform',
          'scale-105',
          'bg-customPurple',
        );
      } else {
        // Applica il gradiente verde quando la lega è diversa da 'All'
        leagueInputRef.current.classList.add(
          'bg-gradient-to-r',
          'from-teal-600',
          'to-teal-500',
          'text-white',
          'border',
          'border-teal-700',
          'transform',
          'scale-105',
        );
      }
    }

    handleButtonClick(selectedBet, selectedDate, selectedLeague);
  }, []);

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);

    const today = new Date().toISOString().split('T')[0];

    if (dateInputRef.current) {
      if (newDate === today) {
        dateInputRef.current.classList.remove(
          'bg-gradient-to-r',
          'from-teal-600',
          'to-teal-500',
          'text-white',
          'border',
          'border-teal-700',
          'transform',
          'scale-105',
          'bg-customPurple',
        );
      } else {
        // Applica il gradiente verde quando la data è diversa da oggi
        dateInputRef.current.classList.add(
          'bg-gradient-to-r',
          'from-teal-600',
          'to-teal-500',
          'text-white',
          'border',
          'border-teal-700',
          'transform',
          'scale-105',
        );
      }
    }

    handleButtonClick(selectedBet, newDate, selectedLeague);
  };

  const handleLeagueChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newVal = event.target.value;
    setSelectedLeague(newVal);

    if (leagueInputRef.current) {
      if (newVal === 'All') {
        leagueInputRef.current.classList.remove(
          'bg-gradient-to-r',
          'from-teal-600',
          'to-teal-500',
          'text-white',
          'border',
          'border-teal-700',
          'transform',
          'scale-105',
          'bg-customPurple',
        );
      } else {
        // Applica il gradiente verde quando la lega è diversa da 'All'
        leagueInputRef.current.classList.add(
          'bg-gradient-to-r',
          'from-teal-600',
          'to-teal-500',
          'text-white',
          'border',
          'border-teal-700',
          'transform',
          'scale-105',
        );
      }
    }

    handleButtonClick(selectedBet, selectedDate, newVal);
  };

  // All the data fetching functions remain the same
  const doGetGoalGoalPredictions = async (date: string, league: string) => {
    const retMatches = await getGoalGoalPredictions(date, league);
    setMatches(retMatches);
  };

  const doGetNoGoalPredictions = async (date: string, league: string) => {
    const retMatches = await getNoGoalPredictions(date, league);
    setMatches(retMatches);
  };

  const doGetOver2Point5Predictions = async (date: string, league: string) => {
    const retMatches = await getOver2Point5Predictions(date, league);
    setMatches(retMatches);
  };

  const doGetUnder3Point5Predictions = async (date: string, league: string) => {
    const retMatches = await getUnder3Point5Predictions(date, league);
    setMatches(retMatches);
  };

  const doGetDrawPredictions = async (date: string, league: string) => {
    const retMatches = await getDrawPredictions(date, league);
    setMatches(retMatches);
  };

  const doGetOver1Point5ResultPredictions = async (
    date: string,
    league: string,
  ) => {
    const retMatches = await getOver1Point5Predictions(date, league);
    setMatches(retMatches);
  };

  const doGetMultigol2_5Predictions = async (date: string, league: string) => {
    const retMatches = await getMultigol2_5Predictions(date, league);
    setMatches(retMatches);
  };

  const doGetMultigol1_3Predictions = async (date: string, league: string) => {
    const retMatches = await getMultigol1_3Predictions(date, league);
    setMatches(retMatches);
  };

  const doGetMultigol1_3HomePredictions = async (
    date: string,
    league: string,
  ) => {
    const retMatches = await getMultigol1_3HomePredictions(date, league);
    setMatches(retMatches);
  };

  const doGetMultigol1_3AwayPredictions = async (
    date: string,
    league: string,
  ) => {
    const retMatches = await getMultigol1_3AwayPredictions(date, league);
    setMatches(retMatches);
  };
  const doGetUnder1Point5HomePredictions = async (
    date: string,
    league: string,
  ) => {
    const retMatches = await getUnder1Point5HomePredictions(date, league);
    setMatches(retMatches);
  };
  const doGetUnder1Point5AwayPredictions = async (
    date: string,
    league: string,
  ) => {
    const retMatches = await getUnder1Point5AwayPredictions(date, league);
    setMatches(retMatches);
  };
  const doGetExactResultPredictions = async (date: string, league: string) => {
    const retMatches = await getExactResultPredictions(date, league);
    setMatches(retMatches);
  };

  const handleButtonClick = async (
    type: string,
    date?: string,
    league?: string,
  ) => {
    setMatchesLoading(true);
    const loadingStartTime = Date.now();
    const searchDate = date || selectedDate;
    let searchLeague = league || selectedLeague;

    if (searchLeague === 'All') searchLeague = '';

    switch (type) {
      case 'GoalGoal':
        await doGetGoalGoalPredictions(searchDate, searchLeague);
        break;
      case 'NoGoal':
        await doGetNoGoalPredictions(searchDate, searchLeague);
        break;
      case 'Over2.5':
        await doGetOver2Point5Predictions(searchDate, searchLeague);
        break;
      case 'Under3.5':
        await doGetUnder3Point5Predictions(searchDate, searchLeague);
        break;
      case 'Draw':
        await doGetDrawPredictions(searchDate, searchLeague);
        break;
      case 'ExactResult':
        await doGetExactResultPredictions(searchDate, searchLeague);
        break;
      case 'Over1.5':
        await doGetOver1Point5ResultPredictions(searchDate, searchLeague);
        break;
      case 'Multigol2_5':
        await doGetMultigol2_5Predictions(searchDate, searchLeague);
        break;
      case 'Multigol1_3':
        await doGetMultigol1_3Predictions(searchDate, searchLeague);
        break;
      case 'Multigol1_3H':
        await doGetMultigol1_3HomePredictions(searchDate, searchLeague);
        break;
      case 'Multigol1_3A':
        await doGetMultigol1_3AwayPredictions(searchDate, searchLeague);
        break;
      case 'Under1.5H':
        await doGetUnder1Point5HomePredictions(searchDate, searchLeague);
        break;
      case 'Under1.5A':
        await doGetUnder1Point5AwayPredictions(searchDate, searchLeague);
        break;
      default:
        break;
    }

    const minLoadingTime = 400;
    const timeout = setTimeout(() => {
      if (Date.now() - loadingStartTime >= minLoadingTime) {
        setMatchesLoading(false);
      }
    }, minLoadingTime);

    await new Promise<void>((resolve) =>
      setTimeout(
        resolve,
        Math.max(0, minLoadingTime - (Date.now() - loadingStartTime)),
      ),
    );

    setMatchesLoading(false);
  };

  // Filter matches by search query
  const filteredMatches = searchQuery
    ? matches.filter((match) => {
        const query = searchQuery.toLowerCase();
        return (
          match.Home.toLowerCase().includes(query) ||
          match.Away.toLowerCase().includes(query) ||
          match.League.toLowerCase().includes(query) ||
          match.Country.toLowerCase().includes(query)
        );
      })
    : matches;
  const { t, i18n } = useTranslation();

  const betTypes = [
    { id: 'GoalGoal', label: t('betTypes.goalGoal'), icon: <FaRandom /> },
    { id: 'Over1.5', label: t('betTypes.over1_5'), icon: <BsGraphUp /> },
    { id: 'Under3.5', label: t('betTypes.under3_5'), icon: <BsGraphDown /> },
    {
      id: 'Multigol2_5',
      label: t('betTypes.multigol2_5'),
      icon: <MdBarChart />,
    },
    {
      id: 'Multigol1_3',
      label: t('betTypes.multigol1_3'),
      icon: <MdAlignVerticalBottom />,
    },
    {
      id: 'Multigol1_3H',
      label: t('betTypes.multigol1_3H'),
      icon: <MdAlignHorizontalLeft />,
    },
    {
      id: 'Multigol1_3A',
      label: t('betTypes.multigol1_3A'),
      icon: <MdAlignHorizontalRight />,
    },
    { id: 'Under1.5H', label: t('betTypes.under1_5H'), icon: <BsGraphDown /> },
    { id: 'Under1.5A', label: t('betTypes.under1_5A'), icon: <BsGraphDown /> },
    {
      id: 'ExactResult',
      label: t('betTypes.exactResult'),
      icon: <MdSportsSoccer />,
    },
  ];

  return (
    <div className="py-14 pt-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-2xl sm:text-5xl font-bold mb-4 mt-5 text-white drop-shadow-lg">
            <FaFutbol className="inline-block mr-4 text-teal-400" />
            {t('titles.soccerMatchPredictions')}
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto">
            {t('titles.homeSubtitle')}
          </p>
        </div>

        {matchesLoading && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-70 backdrop-blur-sm z-50 flex justify-center items-center">
            <div className="bg-white bg-opacity-10 p-6 rounded-xl shadow-2xl flex flex-col items-center">
              <TailSpin color="#6ee7b7" height={50} width={50} />
              <p className="mt-4 text-teal-300 font-medium">
                {t('ui.loadingPredictions')}
              </p>
            </div>
          </div>
        )}

        {/* Campo di ricerca */}
        <div className="w-full max-w-md mx-auto mb-6">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={'🔍 ' + t('ui.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-3 pl-10 pr-4 bg-slate-800 bg-opacity-70 backdrop-blur-sm text-white rounded-full border border-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all shadow-md"
            />
          </div>
        </div>

        <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
          {betTypes.map((betType) => (
            <button
              key={betType.id}
              onClick={() => {
                setSelectedBet(betType.id);
                handleButtonClick(betType.id);
              }}
              className={`flex items-center px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 shadow-md ${
                selectedBet === betType.id
                  ? 'bg-gradient-to-r from-teal-600 to-teal-500 text-white border border-teal-700 transform scale-105'
                  : 'bg-slate-800 text-gray-300 hover:bg-slate-700 hover:text-white border border-gray-700'
              }`}
            >
              <span className="mr-2">{betType.icon}</span>
              {betType.label}
            </button>
          ))}

          {/* Selettori per data e lega */}
          <div className="relative">
            <input
              type="date"
              id="matchDate"
              name="matchDate"
              value={selectedDate}
              onChange={handleDateChange}
              ref={dateInputRef}
              className="appearance-none pl-10 pr-4 py-2 rounded-full text-xs sm:text-sm bg-slate-800 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent shadow-md transition-all"
            />
            <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-teal-400 pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={selectedLeague}
              onChange={handleLeagueChange}
              ref={leagueInputRef}
              className="appearance-none pl-10 pr-4 py-2 rounded-full text-xs sm:text-sm bg-slate-800 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent shadow-md transition-all"
            >
              <option value="All">{t('ui.allLeagues')}</option>
              {leagues.map((league) => (
                <option key={league.id} value={league.id}>
                  {league.name}
                </option>
              ))}
            </select>
            <TbGpsFilled className="absolute left-3 top-1/2 transform -translate-y-1/2 text-teal-400 pointer-events-none" />
          </div>
        </div>

        <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-teal-800 to-indigo-900 py-3 px-4 border-b border-gray-700">
            <h2
              className={`font-bold text-white flex items-center ${
                isMobile ? 'text-sm' : 'text-lg'
              }`}
            >
              <span className="mr-2">
                {betTypes.find((b) => b.id === selectedBet)?.icon}
              </span>
              {betTypes.find((b) => b.id === selectedBet)?.label}{' '}
              {t('ui.predictions')}
              <span className="ml-auto text-sm font-normal text-gray-300">
                {filteredMatches.length} {t('ui.matches')}
              </span>
            </h2>
          </div>

          <div className="overflow-x-auto shadow-md rounded-lg">
            {!matchesLoading && filteredMatches.length > 0 ? (
              <MatchList data={filteredMatches} />
            ) : !matchesLoading ? (
              <div className="text-center py-16">
                <div className="inline-block p-4 rounded-full bg-slate-700 bg-opacity-50 mb-4">
                  <FaSearch className="h-10 w-10 text-gray-400" />
                </div>
                <p className="text-gray-300 text-lg font-medium">
                  {t('ui.noMatchesFound')}
                </p>
                <p className="text-gray-400 mt-2">
                  {t('ui.tryAdjustingFilters')}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainPage;
